<template>
  <div class="synthese__container">
    <SyntheseSection
      title="L'employeur"
      :items="employerDetails"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      title="Le salarié(e)"
      :items="salarieDetails"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      title="Le tuteur au sein de l'établissement employeur"
      :items="tuteurEtablissementDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      title="Le tuteur au sein de l’entreprise utilisatrice"
      :items="tuteurEntrepriseDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      title="Le contrat"
      :items="contratDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      title="Formation"
      :items="formationDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-for="(formationInterne, index) in formationsInterne"
      :key="index"
      title="Formation interne"
      :items="formationInterne"
    />
    <SyntheseSection
      v-for="(formationExterne, index) in formationsExterne"
      :key="index"
      title="Formation externe"
      :items="formationExterne"
    />
  </div>
  <FormContainer>
    <template #content>
      <WrapperCheckbox
        id="field_unknown1"
        v-model="attestationSurHonneurModel"
        :alertLabel="meta.attestationSurHonneur?.$messageErreurDirty!"
        label="J’atteste sur l’honneur l’exactitude des données saisies."
        :readonly
        class="col-36"
      />
      <WrapperCheckbox
        id="field_unknown2"
        v-model="acceptationCgModel"
        :alertLabel="meta.acceptationCg?.$messageErreurDirty!"
        :readonly
        class="col-36"
      >
        <BasicText style="flex: 1">
          J'accepte les Conditions Générales (CG) d'Akto et les conditions de prise en charge, et je confirme avoir pris
          connaissance de celles-ci via le lien suivant : «
          <BasicLink
            label="Conditions générales - Akto"
            @link-click="redirectExterne('https://www.akto.fr/conditions-generales')"
          />
        </BasicText>
        »
      </WrapperCheckbox>
      <div class="form__bloc">
        <WrapperInput
          id="field_240"
          v-model="lieuSignatureModel"
          label="Fait à"
          :required="meta.lieuSignature?.$status === PropertyStatus.Required"
          :alertLabel="meta.lieuSignature?.$messageErreurDirty!"
          class="col-12 col-md-14 col-sm-36"
          deletable
          hint="50 caractères maximum"
          :maxlength="50"
          :readonly
        />
        <WrapperInput
          id="field_unknown"
          v-model="commentaireModel"
          label="Commentaires (infos complémentaires...)"
          :required="meta.commentaire?.$status === PropertyStatus.Required"
          :alertLabel="meta.commentaire?.$messageErreurDirty!"
          class="col-24 col-md-22 col-sm-36"
          deletable
          hint="256 caractères maximum"
          :maxlength="256"
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import type { LovDTO } from '@/features/api/extranet/dto/Lov'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { TypeQualificationLibelle } from '@/features/domaine/types/opco/Demande/CPRO'
  import { useDemandeCPROStore } from '@/features/interface/demande/cpro/useDemandeCPROStore'
  import { FormContainer, SyntheseSection } from '@/features/interface/demande/shared'
  import { getLabelBoolean } from '@/features/interface/shared/tools'
  import { inputDureeToString } from '@designSystem/index'
  import {
    dateFormatter,
    isDate,
    isNotNullUndefinedOrEmptyString,
    isNullOrUndefined,
    formatNumber,
  } from '@/features/shared/tools'
  import { redirectExterne } from '@/features/shared/tools/url'

  const store = useDemandeCPROStore()
  const { setupFieldWithKey } = store
  const { readonly, meta } = storeToRefs(store)

  const lieuSignatureModel = setupFieldWithKey<string>('lieuSignature', true)
  const commentaireModel = setupFieldWithKey<string>('commentaire', true)
  const attestationSurHonneurModel = setupFieldWithKey<boolean>('attestationSurHonneur', true)
  const acceptationCgModel = setupFieldWithKey<boolean>('acceptationCg', true)
  const { model, lov } = useDemandeCPROStore()

  const getLovIntitule = <T extends string | number>(lovDTO: LovDTO<T>, code?: T | null) => {
    if (isNullOrUndefined(code)) return ''
    return lovDTO.find((item) => item.code === code)?.intitule ?? ''
  }

  const getDynamicLovIntitule = <T extends string | number>(lovDTO: LovDTO<T>, code: T | null | undefined) =>
    getLovIntitule(lovDTO, code)?.split('-')?.at(1) ?? ''

  const getDateIntitule = (value: any) => (isDate(value) ? dateFormatter(value) : '')

  const employerDetails = computed(() => ({
    'Nom et prénom ou dénomination': model?.employeur?.etablissement?.denomination,
    Siret: model?.employeur?.etablissement?.siret?.valeur,
    'IDCC applicable': model?.employeur?.etablissement?.idcc?.code,
    Email: model?.employeur?.contact?.email,
    'N° et Voie': model?.employeur?.etablissement?.adresse?.voie,
    Complément: model?.employeur?.etablissement?.adresse?.complement,
    'CP Ville': `${model?.employeur?.etablissement?.adresse?.codePostal} ${model?.employeur?.etablissement?.adresse?.commune} `,
  }))

  const salarieDetails = computed(() => ({
    'Nom de naissance': model?.salarie?.nomNaissance,
    "Nom d'usage": model?.salarie?.nom,
    Prénom: model?.salarie?.prenom,
    'Date de naissance': getDateIntitule(model?.salarie?.dateNaissance),
    Email: model?.salarie?.email ?? '',
  }))

  const tuteurEtablissementDetail = computed(() => ({
    'Nom complet': [model?.tutorat?.tuteurEntrepriseEmployeur?.nom, model?.tutorat?.tuteurEntrepriseEmployeur?.prenom]
      .filter(isNotNullUndefinedOrEmptyString)
      .join(' '),
    'Date de naissance': getDateIntitule(model?.tutorat?.tuteurEntrepriseEmployeur?.dateNaissance),
  }))

  const tuteurEntrepriseDetail = computed(() => ({
    'Nom complet': [
      model?.tutorat?.tuteurEntrepriseUtilisatrice?.nom,
      model?.tutorat?.tuteurEntrepriseUtilisatrice?.prenom,
    ]
      .filter(isNotNullUndefinedOrEmptyString)
      .join(' '),
    'Date de naissance': getDateIntitule(model?.tutorat?.tuteurEntrepriseUtilisatrice?.dateNaissance),
  }))

  const contratDetail = computed(() => ({
    'Nature du contrat': lov.NatureContrat.find((elem) => elem.code === model?.contrat?.natureContrat)?.intitule,
    'Type de contrat ou d’avenant': getDynamicLovIntitule(lov.TypeContratAvenant, model?.contrat?.typeContratAvenant),
    'Si avenant, date d’effet': getDateIntitule(model?.contrat?.dateEffetAvenant),
    'Date de conclusion': getDateIntitule(model?.contrat?.dateConclusion),
    'Date de début d’exécution du contrat': getDateIntitule(model?.contrat?.dateDebutExecutionContrat),
    "Date de fin du CDD ou de l'action de professionnalisation si CDI": getDateIntitule(
      model?.contrat?.dateFinCddOuActionSiCdi,
    ),
    'Durée hebdomadaire du travail': inputDureeToString(model?.contrat?.dureeHebdomadaire),
  }))

  const formationDetail = computed(() => ({
    'Intitule de la formation': model?.formation?.intitule,
    'Type de qualification visée': TypeQualificationLibelle[model!.formation!.typeQualification!],
    'RNCP / Formacode':
      model?.formation?.rncp != null
        ? model!.formation!.rncp!.code! + ' - ' + model!.formation!.rncp!.intitule!
        : model?.formation?.formacode != null
          ? model!.formation!.formacode!.code! + ' - ' + model!.formation!.formacode!.intitule!
          : '-',
    'Diplôme ou titre visé': getDynamicLovIntitule(lov.Diplome, model?.formation?.diplomeOuTitreVise),
  }))

  const modules = model?.formation?.modules ?? []

  type FormationData = {
    [key: string]: string
  }

  const formationsInterne = computed((): FormationData[] => {
    return modules
      .filter((module) => module.typeModule === 'Interne')
      .map((module) => ({
        'Date de début': getDateIntitule(module?.dateDebut),
        'Date de fin de formation': getDateIntitule(module?.dateFin),
        'Durée de la formation': module?.duree ? `${module?.duree}h` : '',
        'Coût formation': `${formatNumber(
          module?.fraisPedagogiques
            ?.filter((x) => x.nature === 'Pedagogique')
            .reduce((acc, frais) => acc + (frais.montant ?? 0), 0),
          { decimal: 2, separateur: true },
        )} €`,
        'Autres types de coûts': `${formatNumber((module?.fraisPedagogiques?.filter((x) => x.nature !== 'Pedagogique').reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0) + (module?.fraisAnnexesSalarie?.reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0), { decimal: 2, separateur: true })} €`,
      }))
  })

  const formationsExterne = computed((): FormationData[] => {
    return modules
      .filter((module) => module.typeModule === 'Externe')
      .map((module) => ({
        'Nom de l’organisme de formation': module?.etablissementPrestataire?.denomination ?? '',
        'Date de début': getDateIntitule(module?.dateDebut),
        'Date de fin de formation': getDateIntitule(module?.dateFin),
        'Durée de la formation': module?.duree ? `${module?.duree}h` : '',
        "Paiement direct à l'organisme de formation": getLabelBoolean(module.avecSubrogation!) ?? '',
        'Coût pédagogique': `${formatNumber(
          module?.fraisPedagogiques
            ?.filter((x) => x.nature === 'Pedagogique')
            .reduce((acc, frais) => acc + (frais.montant ?? 0), 0),
          { decimal: 2, separateur: true },
        )} €`,
        'Autres types de coûts': `${formatNumber((module?.fraisPedagogiques?.filter((x) => x.nature !== 'Pedagogique').reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0) + (module?.fraisAnnexesSalarie?.reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0), { decimal: 2, separateur: true })} €`,
      }))
  })
</script>
<style scoped lang="less">
  .synthese__container {
    width: 90%;
    align-self: center;
    margin: @spacing-20 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    gap: 20px;

    > div {
      width: calc(45% - 10px);
      border-radius: @border-radius-l;

      @media (max-width: @breakpoint-tablet) {
        width: 100%;
      }
    }

    @container (max-width: @breakpoint-mobile) {
      width: 100%;
    }
  }

  .synthese {
    &__bloc {
      margin: @spacing-20 0;
      display: grid;
      grid-template-columns: repeat(36, 1fr);
      grid-row-gap: @spacing-20;
      grid-column-gap: 2%;
    }
  }
</style>

